export const useCountryDetection = () => {
  const { appHostname } = useAppLink();

  const getUserCountryCookie = () => {
    return useCookie("user-country", {
      maxAge: 60 * 60 * 24 * 365,
      path: "/",
      domain: `.${appHostname}`,
    });
  };

  const getClientIP = (event: any): string => {
    // Получаем x-forwarded-for и берем первый IP (реальный клиент)
    const forwardedFor = event.node.req.headers["x-forwarded-for"] as string;
    if (forwardedFor) {
      return forwardedFor.split(",")[0].trim();
    }

    // Альтернативные заголовки
    const realIP = event.node.req.headers["x-real-ip"] as string;
    if (realIP) {
      return realIP.trim();
    }

    // Cloudflare
    const cfConnectingIP = event.node.req.headers["cf-connecting-ip"] as string;
    if (cfConnectingIP) {
      return cfConnectingIP.trim();
    }

    // Последний вариант - может быть IP прокси
    return event.node.req.socket.remoteAddress || "";
  };

  const isLocalIP = (ip: string): boolean => {
    if (!ip) return true;

    // Список локальных IP адресов
    const localIPs = [
      "127.0.0.1",
      "localhost",
      "::1", // IPv6 localhost
      "0.0.0.0",
    ];

    // Проверяем точные совпадения
    if (localIPs.includes(ip)) {
      return true;
    }

    // Проверяем локальные сети
    if (
      ip.startsWith("192.168.") || // Частная сеть
      ip.startsWith("10.") || // Частная сеть
      ip.startsWith("172.16.") || // Частная сеть
      ip.startsWith("172.17.") || // Docker
      ip.startsWith("172.18.") || // Docker
      ip.startsWith("172.19.") || // Docker
      ip.startsWith("172.20.") || // Частная сеть
      ip.startsWith("172.21.") || // Частная сеть
      ip.startsWith("172.22.") || // Частная сеть
      ip.startsWith("172.23.") || // Частная сеть
      ip.startsWith("172.24.") || // Частная сеть
      ip.startsWith("172.25.") || // Частная сеть
      ip.startsWith("172.26.") || // Частная сеть
      ip.startsWith("172.27.") || // Частная сеть
      ip.startsWith("172.28.") || // Частная сеть
      ip.startsWith("172.29.") || // Частная сеть
      ip.startsWith("172.30.") || // Частная сеть
      ip.startsWith("172.31.") // Частная сеть
    ) {
      return true;
    }

    return false;
  };

  const detectCountryByHeaders = (event: any): string => {
    return (
      (event.node.req.headers["cf-ipcountry"] as string) || // Cloudflare
      (event.node.req.headers["x-country-code"] as string) || // Некоторые прокси
      (event.node.req.headers["x-geo-country"] as string) || // Некоторые CDN
      ""
    );
  };

  const detectCountryByIP = async (clientIP: string): Promise<string> => {
    // Проверяем, не локальный ли это IP
    if (isLocalIP(clientIP)) {
      console.log(`Skipping country detection for local IP: ${clientIP}`);
      return "";
    }

    try {
      console.log(`Detecting country for IP: ${clientIP}`);
      const countryFromIP = await $fetch(`https://api.country.is/${clientIP.split(",")[0].trim()}`);
      return countryFromIP?.country || "";
    } catch (error) {
      console.error("Failed to determine country by IP:", error);
      return "";
    }
  };

  const detectAndSetCountry = async (): Promise<void> => {
    const userCountryCookie = getUserCountryCookie();

    if (userCountryCookie.value) {
      return;
    }

    const event = useRequestEvent();

    if (!event?.node.req.headers) {
      userCountryCookie.value = "GE";
      return;
    }

    // Пробуем заголовки геолокации
    const countryFromHeaders = detectCountryByHeaders(event);
    if (countryFromHeaders) {
      userCountryCookie.value = countryFromHeaders;
      return;
    }

    // Пробуем определить по IP
    const clientIP = getClientIP(event);
    console.log(`Client IP: ${clientIP}`);

    if (clientIP && !isLocalIP(clientIP)) {
      const countryFromIP = await detectCountryByIP(clientIP);
      if (countryFromIP) {
        userCountryCookie.value = countryFromIP;
        return;
      }
    } else if (isLocalIP(clientIP)) {
      console.log("Local development detected, using fallback country");
    }

    // Fallback
    userCountryCookie.value = "GE";
  };

  return {
    getUserCountryCookie,
    detectAndSetCountry,
    getClientIP,
    detectCountryByHeaders,
    detectCountryByIP,
    isLocalIP,
  };
};
